/**
 * CatalogLineage — GVW-125 Lineage / provenance page
 * 
 * Per brief.md: Workspace shell page with center = lineage graph; right panel = focused 
 * node detail; header card with dataset name + actions. Graph shows upload-to-render chain.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { catalogClient, type Lineage, type LineageNode, type CatalogResult } from '../../catalog/catalogClient';

interface CatalogLineageProps {
  className?: string;
}

interface CatalogLineageState {
  lineage: Lineage | null;
  loading: boolean;
  error: string | null;
  selectedNode: LineageNode | null;
  viewMode: 'graph' | 'table';
}

export function CatalogLineage({ className }: CatalogLineageProps): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<CatalogLineageState>({
    lineage: null,
    loading: true,
    error: null,
    selectedNode: null,
    viewMode: 'graph',
  });

  const loadLineage = useCallback(async () => {
    if (!id) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'No dataset ID provided',
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    
    const result: CatalogResult<Lineage> = await catalogClient.getLineage(id);
    
    if (result.ok) {
      setState(prev => ({
        ...prev,
        lineage: result.data,
        loading: false,
      }));
    } else {
      let errorMessage = 'Failed to load lineage';
      if (result.reason === 'backend-not-deployed') {
        errorMessage = 'Backend not deployed. The lineage service is not available.';
      } else if (result.reason === 'not-found') {
        errorMessage = 'Lineage not found for this dataset.';
      } else if (result.reason === 'unauthorized') {
        errorMessage = 'You do not have permission to access this lineage.';
      }
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [id]);

  useEffect(() => {
    loadLineage();
  }, [id, loadLineage]);

  const handleNodeSelect = (node: LineageNode) => {
    setState(prev => ({ ...prev, selectedNode: node }));
  };

  const toggleViewMode = () => {
    setState(prev => ({
      ...prev,
      viewMode: prev.viewMode === 'graph' ? 'table' : 'graph',
    }));
  };

  const getNodeIcon = (type: LineageNode['type']) => {
    switch (type) {
      case 'upload': return 'upload_file';
      case 'validate': return 'fact_check';
      case 'conversion': return 'sync_alt';
      case 'tile': return 'grid_on';
      case 'render': return 'visibility';
      default: return 'help';
    }
  };

  const getStatusIcon = (status: LineageNode['status']) => {
    switch (status) {
      case 'pending': return 'schedule';
      case 'running': return 'play_arrow';
      case 'completed': return 'check_circle';
      case 'failed': return 'error';
      default: return 'help';
    }
  };

  const getStatusColor = (status: LineageNode['status']) => {
    switch (status) {
      case 'pending': return 'neutral';
      case 'running': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'neutral';
    }
  };

  if (state.loading) {
    return (
      <div className={`catalog-lineage ${className || ''}`} aria-busy="true">
        <div className="catalog-lineage__loading">
          <div className="catalog-lineage__skeleton-header" />
          <div className="catalog-lineage__skeleton-graph" />
          <div className="catalog-lineage__skeleton-panel" />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`catalog-lineage ${className || ''}`} role="alert">
        <div className="catalog-lineage__error">
          <span className="material-icons" aria-hidden="true">error</span>
          <h1>We could not load lineage</h1>
          <p>{state.error}</p>
          <button onClick={loadLineage} type="button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!state.lineage || state.lineage.nodes.length === 0) {
    return (
      <div className={`catalog-lineage ${className || ''}`}>
        <div className="catalog-lineage__empty">
          <span className="material-icons" aria-hidden="true">account_tree</span>
          <h1>No lineage yet</h1>
          <p>Upload and process data to see the lineage graph.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`catalog-lineage ${className || ''}`} data-testid="catalog-lineage">
      {/* Header card */}
      <header className="catalog-lineage__header">
        <div className="catalog-lineage__header-content">
          <h1>
            <span className="material-icons" aria-hidden="true">account_tree</span>
            Data Lineage
          </h1>
          <p>Dataset ID: {id}</p>
        </div>
        
        <div className="catalog-lineage__header-actions">
          <button
            type="button"
            onClick={() => {
              // TODO: Navigate to dataset detail
              console.log('Open dataset detail:', id);
            }}
          >
            Open dataset detail
          </button>
          
          <button
            type="button"
            onClick={() => {
              // TODO: Navigate to audit page
              console.log('Open audit:', id);
            }}
          >
            <span className="material-icons" aria-hidden="true">history</span>
            Open audit
          </button>
        </div>
      </header>

      <div className="catalog-lineage__content">
        {/* Main graph/table area */}
        <main className="catalog-lineage__main">
          <div className="catalog-lineage__toolbar">
            <button
              type="button"
              onClick={toggleViewMode}
              className="catalog-lineage__view-toggle"
              aria-pressed={state.viewMode === 'table'}
            >
              <span className="material-icons" aria-hidden="true">
                {state.viewMode === 'graph' ? 'table_view' : 'account_tree'}
              </span>
              View as {state.viewMode === 'graph' ? 'table' : 'graph'}
            </button>
          </div>

          {state.viewMode === 'graph' ? (
            <div className="catalog-lineage__graph-container">
              <svg className="catalog-lineage__graph" viewBox="0 0 800 400">
                {/* Render edges first */}
                {state.lineage.edges.map((edge, index) => {
                  const fromIndex = state.lineage!.nodes.findIndex(n => n.id === edge.from);
                  const toIndex = state.lineage!.nodes.findIndex(n => n.id === edge.to);
                  
                  if (fromIndex === -1 || toIndex === -1) return null;
                  
                  const fromX = 100 + fromIndex * 150;
                  const toX = 100 + toIndex * 150;
                  const y = 200;
                  
                  return (
                    <line
                      key={index}
                      x1={fromX + 40}
                      y1={y}
                      x2={toX - 40}
                      y2={y}
                      className="catalog-lineage__edge"
                      markerEnd="url(#arrowhead)"
                    />
                  );
                })}
                
                {/* Arrow marker definition */}
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" className="catalog-lineage__arrow" />
                  </marker>
                </defs>
                
                {/* Render nodes */}
                {state.lineage.nodes.map((node, index) => {
                  const x = 100 + index * 150;
                  const y = 200;
                  const isSelected = state.selectedNode?.id === node.id;
                  
                  return (
                    <g key={node.id}>
                      <circle
                        cx={x}
                        cy={y}
                        r={30}
                        className={`catalog-lineage__node catalog-lineage__node--${getStatusColor(node.status)} ${
                          isSelected ? 'catalog-lineage__node--selected' : ''
                        }`}
                        tabIndex={0}
                        role="button"
                        aria-label={`Lineage step: ${node.type}; status ${node.status}; at ${node.timestamp}`}
                        onClick={() => handleNodeSelect(node)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleNodeSelect(node);
                          }
                        }}
                      />
                      
                      <text
                        x={x}
                        y={y - 45}
                        textAnchor="middle"
                        className="catalog-lineage__node-label"
                      >
                        {node.type}
                      </text>
                      
                      <text
                        x={x}
                        y={y + 50}
                        textAnchor="middle"
                        className="catalog-lineage__node-status"
                      >
                        {node.status}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          ) : (
            <div className="catalog-lineage__table-container">
              <table className="catalog-lineage__table">
                <caption>Lineage steps for dataset {id}</caption>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Timestamp</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {state.lineage.nodes.map((node, index) => (
                    <tr 
                      key={node.id}
                      className={state.selectedNode?.id === node.id ? 'catalog-lineage__table-row--selected' : ''}
                    >
                      <td>{index + 1}</td>
                      <td>
                        <span className="catalog-lineage__type-cell">
                          <span className="material-icons" aria-hidden="true">
                            {getNodeIcon(node.type)}
                          </span>
                          {node.type}
                        </span>
                      </td>
                      <td>
                        <span className={`catalog-lineage__status-chip catalog-lineage__status-chip--${getStatusColor(node.status)}`}>
                          <span className="material-icons" aria-hidden="true">
                            {getStatusIcon(node.status)}
                          </span>
                          {node.status}
                        </span>
                      </td>
                      <td>{new Date(node.timestamp).toLocaleString()}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleNodeSelect(node)}
                          aria-label={`View details for ${node.type} step`}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Show edges in table format */}
              <div className="catalog-lineage__edges-info">
                <h3>Data Flow</h3>
                <ul>
                  {state.lineage.edges.map((edge, index) => {
                    const fromNode = state.lineage!.nodes.find(n => n.id === edge.from);
                    const toNode = state.lineage!.nodes.find(n => n.id === edge.to);
                    
                    return (
                      <li key={index}>
                        {fromNode?.type} → {toNode?.type}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </main>

        {/* Right panel - node details */}
        {state.selectedNode && (
          <aside 
            className="catalog-lineage__detail-panel" 
            role="region" 
            aria-labelledby="detail-panel-heading"
          >
            <h2 id="detail-panel-heading">Step Details</h2>
            
            <div className="catalog-lineage__detail-content">
              <div className="catalog-lineage__detail-header">
                <h3>
                  <span className="material-icons" aria-hidden="true">
                    {getNodeIcon(state.selectedNode.type)}
                  </span>
                  {state.selectedNode.type}
                </h3>
                
                <span className={`catalog-lineage__detail-status catalog-lineage__detail-status--${getStatusColor(state.selectedNode.status)}`}>
                  <span className="material-icons" aria-hidden="true">
                    {getStatusIcon(state.selectedNode.status)}
                  </span>
                  {state.selectedNode.status}
                </span>
              </div>
              
              <div className="catalog-lineage__detail-info">
                <div className="catalog-lineage__detail-field">
                  <dt>Timestamp</dt>
                  <dd>{new Date(state.selectedNode.timestamp).toLocaleString()}</dd>
                </div>
                
                <div className="catalog-lineage__detail-field">
                  <dt>Step ID</dt>
                  <dd>{state.selectedNode.id}</dd>
                </div>
                
                {state.selectedNode.metadata && (
                  <div className="catalog-lineage__detail-field">
                    <dt>Metadata</dt>
                    <dd>
                      <details>
                        <summary>View metadata</summary>
                        <pre className="catalog-lineage__metadata">
                          {JSON.stringify(state.selectedNode.metadata, null, 2)}
                        </pre>
                      </details>
                    </dd>
                  </div>
                )}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}